import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const FAQ = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How much can I really save on business internet?',
      answer: 'Our clients save an average of 40% on their monthly internet bills. Actual savings vary based on your current provider, contract terms, and available options in your area, but we typically find savings of 30-50% for most businesses.'
    },
    {
      question: 'Is your service really free? What\'s the catch?',
      answer: 'Yes, our service is completely free for businesses. We\'re compensated by internet providers when we connect them with customers. This means you get expert negotiation and better rates at no cost to you. No hidden fees, no obligations.'
    },
    {
      question: 'Which internet providers do you work with?',
      answer: 'We work with all major business internet providers in the Hudson Valley, including Spectrum, Verizon Business, Optimum, EarthLink, and numerous local providers. Our goal is to find you the best option regardless of which provider offers it.'
    },
    {
      question: 'How long does the process take?',
      answer: 'The initial analysis and quote typically takes 24-48 hours. If you decide to proceed with a new provider, the installation process varies but usually takes 1-2 weeks. We handle all the coordination to minimize downtime.'
    },
    {
      question: 'Do I have to switch providers to save money?',
      answer: 'Not necessarily. In many cases, we can negotiate better rates with your current provider. However, if another provider offers significantly better pricing or service, we\'ll present those options so you can make an informed decision.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We serve the entire Hudson Valley region, including Poughkeepsie, Kingston, Newburgh, Middletown, Beacon, and surrounding areas. If you\'re in the Hudson Valley, we can help. Contact us to confirm coverage in your specific location.'
    },
    {
      question: 'What information do you need from me?',
      answer: 'We start with a copy of your current internet bill and basic information about your business needs (number of employees, internet usage, etc.). This helps us identify the best options and potential savings for your specific situation.'
    },
    {
      question: 'Can you help with other business services?',
      answer: 'While our specialty is business internet, we also have partnerships for business phone systems, cloud services, and other technology solutions. Let us know what you need and we\'ll connect you with the right options.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Questions
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about saving on business internet in the Hudson Valley
          </p>
        </motion.div>

        <div className="space-y-4 mb-16">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-gray-50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SafeIcon icon={FiIcons.FiChevronDown} className="w-5 h-5 text-blue-600 flex-shrink-0" />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-white"
        >
          <h3 className="text-3xl font-bold mb-4">
            Still have questions?
          </h3>
          <p className="text-xl mb-8 opacity-90">
            Contact us for personalized answers and a free savings analysis
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Contact Us
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;